import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import maplibregl, { Map as MapLibreMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Route, MapPin, Navigation, Clock, Coffee, ClipboardCheck } from 'lucide-react';
import { useHasModule } from '../store/modules';
import { vrpApi, type VrpTour } from '../services/api';

const TOUR_COLOR = '#06b6d4';
const DEFAULT_CENTER: [number, number] = [2.3522, 48.8566];

function decodePolyline(str: string, precision = 5): Array<[number, number]> {
  let index = 0, lat = 0, lng = 0;
  const factor = Math.pow(10, precision);
  const out: Array<[number, number]> = [];
  while (index < str.length) {
    let result = 0, shift = 0, b: number;
    do { b = str.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : (result >> 1);
    result = 0; shift = 0;
    do { b = str.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : (result >> 1);
    out.push([lng / factor, lat / factor]);
  }
  return out;
}

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h${m.toString().padStart(2, '0')}` : `${m} min`;
}

function formatDistance(meters: number): string {
  return meters >= 1000 ? `${(meters / 1000).toFixed(1)} km` : `${meters} m`;
}

/** Build a Waze deep link to the given coordinates. Falls back to Google Maps on iOS. */
function navLink(lat: number, lng: number): string {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (isIOS) return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
  return `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`;
}

export default function MyToursPage() {
  const { t } = useTranslation();
  const hasVrpModule = useHasModule('VRP');
  const [date, setDate] = useState<string>(() => new Date().toISOString().slice(0, 10));

  const { data: tours, isLoading } = useQuery({
    queryKey: ['my-tours', date],
    queryFn: () => vrpApi.listMyTours({ date }),
    enabled: hasVrpModule,
    refetchInterval: 30_000, // pick up dispatcher changes every 30s
  });

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);

  useEffect(() => {
    if (!hasVrpModule) return;
    if (!mapContainerRef.current || mapRef.current) return;
    mapRef.current = new maplibregl.Map({
      container: mapContainerRef.current,
      style: {
        version: 8,
        sources: {
          'raster-tiles': {
            type: 'raster',
            tiles: [
              'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
              'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
              'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
            ],
            tileSize: 256,
            attribution: '&copy; OpenStreetMap',
          },
        },
        layers: [{ id: 'raster-layer', type: 'raster', source: 'raster-tiles', minzoom: 0, maxzoom: 19 }],
      },
      center: DEFAULT_CENTER,
      zoom: 6,
    });
    mapRef.current.addControl(new maplibregl.NavigationControl(), 'top-right');
    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, [hasVrpModule]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !tours) return;
    if (!map.isStyleLoaded()) {
      map.once('load', () => renderTours(map, tours));
      return;
    }
    renderTours(map, tours);
  }, [tours]);

  const flatStops = useMemo(
    () => (tours ?? []).flatMap((t, ti) => t.stops.map((s) => ({ ...s, tourIdx: ti, vehicleName: t.vehicle?.name ?? '' }))),
    [tours],
  );

  if (!hasVrpModule) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-amber-800">
          <h2 className="font-semibold mb-1">{t('my_tours.module_off_title')}</h2>
          <p className="text-sm">{t('my_tours.module_off_body')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Route className="h-6 w-6 sm:h-7 sm:w-7 text-primary-600" />
          {t('my_tours.title')}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{t('my_tours.subtitle')}</p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <label className="block text-xs font-medium text-gray-700 mb-1">{t('my_tours.date')}</label>
        <input
          type="date" value={date} onChange={(e) => setDate(e.target.value)}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
        />
      </div>

      <div ref={mapContainerRef} className="w-full h-[400px] rounded-lg overflow-hidden border border-gray-200 shadow-sm" />

      {isLoading && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 text-center text-sm text-gray-500">
          {t('my_tours.loading')}
        </div>
      )}

      {!isLoading && (tours ?? []).length === 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
          {t('my_tours.no_tour')}
        </div>
      )}

      {(tours ?? []).map((tour, ti) => (
        <div key={tour.id} className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
            <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: TOUR_COLOR }} />
              {tour.vehicle?.name ?? t('my_tours.tour_n', { n: ti + 1 })}
              {tour.vehicle?.plate && <span className="text-xs font-mono text-gray-500">{tour.vehicle.plate}</span>}
            </div>
            <div className="text-xs text-gray-500 flex gap-3">
              <span>{formatDistance(tour.totalDistanceM)}</span>
              {/* totalDurationS = travel + breaks (no service). Add service for the wall-clock total. */}
              <span title={t('my_tours.total_duration_tooltip')}>{formatDuration(tour.totalDurationS + tour.totalServiceS)}</span>
            </div>
          </div>
          <ol className="divide-y divide-gray-100">
            {(() => {
              let visIdx = 0;
              return tour.stops.map((s) => {
                if (s.isBreak) {
                  return (
                    <li key={s.id} className="px-4 py-3 flex items-center gap-3 bg-amber-50/40 text-amber-900">
                      <span className="w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
                        <Coffee className="w-3.5 h-3.5" />
                      </span>
                      <div className="flex-1">
                        <div className="text-sm font-medium">{t('my_tours.break_label')}</div>
                        <div className="text-xs text-amber-700 flex gap-2 mt-0.5">
                          {s.plannedArrival && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(s.plannedArrival).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                          <span>{formatDuration(s.serviceDurationS)}</span>
                        </div>
                      </div>
                    </li>
                  );
                }
                visIdx += 1;
                const tourDate = new Date(tour.date).toISOString().slice(0, 10);
                const status = s.mission?.status;
                const isDone = status === 'COMPLETED';
                const isCancelled = status === 'CANCELLED';
                const isInProgress = status === 'IN_PROGRESS';
                const actionDisabled = isDone || isCancelled;
                return (
                  <li key={s.id} className={`px-4 py-3 flex items-start gap-3 ${isDone ? 'opacity-60' : ''}`}>
                    <span
                      className="w-6 h-6 mt-0.5 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: isDone ? '#10b981' : isCancelled ? '#9ca3af' : TOUR_COLOR }}
                    >{isDone ? '✓' : visIdx}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-900 truncate flex items-center gap-1.5 flex-wrap">
                        <span className={`truncate ${isDone ? 'line-through text-gray-500' : ''}`}>
                          {s.label ?? s.address ?? `${s.latitude.toFixed(4)}, ${s.longitude.toFixed(4)}`}
                        </span>
                        {isDone && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100 flex-shrink-0 font-medium">
                            {t('my_tours.status_done')}
                          </span>
                        )}
                        {isInProgress && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-100 flex-shrink-0 font-medium">
                            {t('my_tours.status_in_progress')}
                          </span>
                        )}
                        {isCancelled && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-100 flex-shrink-0 font-medium">
                            {t('my_tours.status_cancelled')}
                          </span>
                        )}
                      </div>
                      {s.address && s.label && (
                        <div className="text-xs text-gray-500 truncate">{s.address}</div>
                      )}
                      <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
                        {s.plannedArrival && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(s.plannedArrival).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                        {s.serviceDurationS > 0 && (
                          <span>{formatDuration(s.serviceDurationS)}</span>
                        )}
                      </div>
                      <div className="mt-2 flex gap-2">
                        <a
                          href={navLink(s.latitude, s.longitude)}
                          target="_blank" rel="noopener noreferrer"
                          className="px-2.5 py-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-xs font-medium flex items-center gap-1"
                          title={t('my_tours.navigate')}
                        >
                          <Navigation className="w-3 h-3" /> {t('my_tours.go_button')}
                        </a>
                        {s.missionId && (
                          actionDisabled ? (
                            <span
                              className="px-2.5 py-1.5 rounded-md bg-gray-200 text-gray-500 text-xs font-medium flex items-center gap-1 cursor-not-allowed"
                              title={isDone ? t('my_tours.mission_done') : t('my_tours.mission_cancelled')}
                            >
                              <ClipboardCheck className="w-3 h-3" />
                              {isDone ? t('my_tours.label_done') : t('my_tours.label_cancelled')}
                            </span>
                          ) : (
                            <Link
                              to={`/my-missions?focus=${s.missionId}&date=${tourDate}`}
                              className={`px-2.5 py-1.5 rounded-md text-white text-xs font-medium flex items-center gap-1 ${
                                isInProgress ? 'bg-amber-500 hover:bg-amber-600' : 'bg-emerald-500 hover:bg-emerald-600'
                              }`}
                              title={t('my_tours.open_mission')}
                            >
                              <ClipboardCheck className="w-3 h-3" />
                              {isInProgress ? t('my_tours.resume') : t('my_tours.execute')}
                            </Link>
                          )
                        )}
                      </div>
                    </div>
                  </li>
                );
              });
            })()}
          </ol>
        </div>
      ))}

      {flatStops.length > 0 && (
        <p className="text-center text-xs text-gray-400 pt-2">
          <MapPin className="w-3 h-3 inline" /> {t('my_tours.stops_total', { count: flatStops.length })}
        </p>
      )}
    </div>
  );
}

function renderTours(map: MapLibreMap, tours: VrpTour[]) {
  const style = map.getStyle();
  for (const layer of style.layers ?? []) {
    if (layer.id.startsWith('vrp-tour-')) map.removeLayer(layer.id);
  }
  for (const sourceId of Object.keys(style.sources ?? {})) {
    if (sourceId.startsWith('vrp-tour-')) map.removeSource(sourceId);
  }
  document.querySelectorAll('.vrp-stop-marker').forEach((el) => el.remove());

  if (!tours || tours.length === 0) return;
  const allCoords: Array<[number, number]> = [];

  tours.forEach((tour) => {
    const polyline = tour.geometry?.polyline;
    let coords: Array<[number, number]> = [];
    if (polyline) coords = decodePolyline(polyline);
    if (coords.length > 0) {
      const sourceId = `vrp-tour-${tour.id}`;
      map.addSource(sourceId, { type: 'geojson', data: { type: 'Feature', geometry: { type: 'LineString', coordinates: coords }, properties: {} } });
      map.addLayer({ id: `vrp-tour-line-${tour.id}`, type: 'line', source: sourceId, paint: { 'line-color': TOUR_COLOR, 'line-width': 5, 'line-opacity': 0.9 } });
      allCoords.push(...coords);
    }
    tour.stops.forEach((s) => {
      const el = document.createElement('div');
      el.className = 'vrp-stop-marker';
      el.style.cssText = `width:24px;height:24px;border-radius:50%;background:${TOUR_COLOR};color:white;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:bold;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.4)`;
      el.textContent = String(s.sequence);
      new maplibregl.Marker({ element: el })
        .setLngLat([s.longitude, s.latitude])
        .addTo(map);
      allCoords.push([s.longitude, s.latitude]);
    });
  });

  if (allCoords.length > 0) {
    const lngs = allCoords.map((c) => c[0]);
    const lats = allCoords.map((c) => c[1]);
    map.fitBounds(
      [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
      { padding: 60, duration: 600 },
    );
  }
}
